import React from 'react';
import { Col, Empty, Row, Table, Tabs } from 'antd';
import PropertyLabel from './ProperyLabel';
import { FieldViewProxy, FieldEditProxy } from '../adms/FieldProxies';

const { TabPane } = Tabs;

const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
const OWL_OBJECT_PROPERTY = 'http://www.w3.org/2002/07/owl#ObjectProperty';
const OWL_DATATYPE_PROPERTY = 'http://www.w3.org/2002/07/owl#DatatypeProperty';
const OWL_ANNOTATION_PROPERTY = 'http://www.w3.org/2002/07/owl#AnnotationProperty';
const RDF_PROPERTY = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#Property';

const getValue = (binding, field) => {
  return binding && binding[field] ? binding[field].value : '';
};

const getLabel = (entity, lang) => {
  if (!entity) return '';
  const labels = Array.isArray(entity.labels) ? entity.labels : [];
  const langLabel = labels.find(label => label.lang === lang);
  if (langLabel) return langLabel.value;
  const emptyLabel = labels.find(label => !label.lang);
  if (emptyLabel) return emptyLabel.value;
  if (labels.length > 0) return labels[0].value;
  return entity.label || entity.iri;
};

const unique = (values) => Array.from(new Set((values || []).filter(Boolean)));

const makeRows = (bindings) => {
  return (bindings || []).map((binding, index) => ({
    key: index,
    subject: getValue(binding, 'subject'),
    predicat: getValue(binding, 'predicat'),
    object: getValue(binding, 'object'),
    objectType: binding.object ? binding.object.type : '',
    lang: binding.object ? binding.object['xml:lang'] : '',
  }));
};

const columns = [
  {title: 'Predicate', dataIndex: 'predicat', key: 'predicat'},
  {title: 'Object', dataIndex: 'object', key: 'object'},
  {title: 'Lang', dataIndex: 'lang', key: 'lang', width: 90},
  {title: 'Type', dataIndex: 'objectType', key: 'objectType', width: 110},
];

const sortedAnnotationStart = [
  'http://www.w3.org/2000/01/rdf-schema#label',
  'http://purl.org/dc/terms/title',
  'http://purl.org/dc/terms/description',
  'http://www.w3.org/2000/01/rdf-schema#comment',
  'http://www.w3.org/2002/07/owl#versionInfo',
];

const getPropertyTypes = (propertyDetails, predicat) => {
  const details = propertyDetails && propertyDetails[predicat];
  return details && Array.isArray(details.types) ? details.types : [];
};

const groupBindingsByPredicate = (bindings) => {
  const groups = {};
  (bindings || []).forEach(binding => {
    const predicat = getValue(binding, 'predicat');
    if (!predicat) return;
    if (!groups[predicat]) groups[predicat] = [];
    groups[predicat].push(binding);
  });
  return groups;
};

const getPredicateGroup = (predicat, bindings, propertyDetails) => {
  const types = getPropertyTypes(propertyDetails, predicat);
  if (types.indexOf(OWL_ANNOTATION_PROPERTY) >= 0) return 'annotation';
  if (types.indexOf(OWL_DATATYPE_PROPERTY) >= 0) return 'datatype';
  if (types.indexOf(OWL_OBJECT_PROPERTY) >= 0) return 'object';

  const hasUriObject = (bindings || []).some(binding => binding.object && binding.object.type === 'uri');
  const hasLiteralObject = (bindings || []).some(binding => binding.object && binding.object.type === 'literal');

  if (hasLiteralObject) return 'datatype';
  if (hasUriObject) return 'object';
  if (types.indexOf(RDF_PROPERTY) >= 0) return 'object';
  return 'annotation';
};

const comparePredicates = (a, b) => {
  const indexA = sortedAnnotationStart.indexOf(a);
  const indexB = sortedAnnotationStart.indexOf(b);
  if (indexA >= 0 && indexB >= 0) return indexA - indexB;
  if (indexA >= 0) return -1;
  if (indexB >= 0) return 1;
  return a.localeCompare(b);
};

class OntologyEntityEditor extends React.Component {
  renderFieldRow(predicat, values) {
    const { factory, lang, operation, iri, vselected, onReload } = this.props;
    const FieldProxy = operation === "edit" ? FieldEditProxy : FieldViewProxy;

    return (
      <Row key={predicat} className="assetCardTable_tr">
        <Col span={10} className="assetCardTable_td">
          <PropertyLabel iri={predicat} factory={factory} isEdit={operation === "edit"} />
        </Col>
        <Col span={14} className="assetCardTable_td" style={{display: 'block'}}>
          <FieldProxy
            factory={factory}
            lang={lang}
            subject={iri}
            predicat={predicat}
            values={values}
            vselected={vselected}
            selected={operation === "edit" ? predicat : undefined}
            onReload={onReload}
            action={this.props.action}
          />
        </Col>
      </Row>
    );
  }

  renderInfo(realEntity, realDetails) {
    const groups = groupBindingsByPredicate(realDetails.outgoing);
    const typeBindings = groups[RDF_TYPE] || [];
    const predicates = Object.keys(groups).filter(predicat => predicat !== RDF_TYPE);
    const addedPredicates = (this.props.addedPredicates || [])
      .filter(predicat => predicat !== RDF_TYPE && !groups[predicat]);
    const groupedPredicates = {
      annotation: [],
      datatype: [],
      object: [],
    };

    predicates.forEach(predicat => {
      const group = getPredicateGroup(predicat, groups[predicat], this.props.propertyDetails);
      groupedPredicates[group].push(predicat);
    });

    groupedPredicates.annotation.sort(comparePredicates);
    groupedPredicates.datatype.sort(comparePredicates);
    groupedPredicates.object.sort(comparePredicates);

    const typeValues = typeBindings.length > 0
      ? typeBindings
      : unique(realEntity.types).map(type => ({
          predicat: {type: 'uri', value: RDF_TYPE},
          object: {type: 'uri', value: type},
        }));

    return (
      <span>
        <Row className="assetCardTable_tr">
          <Col span={10} className="assetCardTable_td"><div className="csi_field_div">URI</div></Col>
          <Col span={14} className="assetCardTable_td">
            <div className="csi_value_div">{this.props.iri}</div>
          </Col>
        </Row>
        <Row className="assetCardTable_tr">
          <Col span={10} className="assetCardTable_td">
            <PropertyLabel iri={RDF_TYPE} factory={this.props.factory} />
          </Col>
          <Col span={14} className="assetCardTable_td" style={{display: 'block'}}>
            <FieldViewProxy
              factory={this.props.factory}
              lang={this.props.lang}
              subject={this.props.iri}
              predicat={RDF_TYPE}
              values={typeValues}
            />
          </Col>
        </Row>
        {groupedPredicates.annotation.map(predicat => this.renderFieldRow(predicat, realDetails.outgoing || []))}
        {groupedPredicates.datatype.map(predicat => this.renderFieldRow(predicat, realDetails.outgoing || []))}
        {groupedPredicates.object.map(predicat => this.renderFieldRow(predicat, realDetails.outgoing || []))}
        {addedPredicates.map(predicat => this.renderFieldRow(predicat, realDetails.outgoing || []))}
      </span>
    );
  }

  render() {
    const { iri, entity, details, lang, loading } = this.props;
    if (!iri) return <Empty description="Элемент не выбран" />;

    const realEntity = entity || {iri};
    const realDetails = details || {};
    const outgoingRows = makeRows(realDetails.outgoing);
    const incomingRows = makeRows(realDetails.incoming);

    return (
      <div>
        <div style={{width: '100%', marginBottom: 12}}>
          <strong>{getLabel(realEntity, lang) || iri}</strong>
        </div>

        <Tabs defaultActiveKey="info" type="card">
          <TabPane tab="Info" key="info">
            {this.renderInfo(realEntity, realDetails)}
          </TabPane>
          <TabPane tab="Triples" key="triples">
            <div>
              <h3>Outgoing</h3>
              <Table size="small" loading={loading} columns={columns} dataSource={outgoingRows} pagination={false} />
            </div>
            <div style={{marginTop: 16}}>
              <h3>Incoming</h3>
              <Table size="small" loading={loading} columns={columns} dataSource={incomingRows} pagination={false} />
            </div>
          </TabPane>
        </Tabs>
      </div>
    );
  }
}

export default OntologyEntityEditor;
